import { useState, useEffect, useCallback } from 'react';
import { formatDecimal } from '../lib/utils';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  Search,
  Plus,
  Filter,
  Edit,
  Trash2,
  Users,
  Clock,
  Calendar,
  GraduationCap,
  FileText,
  AlertTriangle,
  Building2,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../core/apiClient';
import { ExcelFilterDropdown } from '../components/ui/excel-filter';

// Types
interface Employee {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  dni: string;
  department?: string;
  position?: string;
  category?: string;
  phone?: string;
  workEmail?: string;
  hireDate: string | null;
  status: 'active' | 'on_leave' | 'terminated';
  // Catàleg "Tipus de Contracte" (Admin > Taules > RRHH) — codi lliure.
  contractType: string;
  hourlyCost: number;
}

// Config
const statusConfig: Record<Employee['status'], { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' }> = {
  active: { label: 'Actiu', variant: 'success' },
  on_leave: { label: 'De baixa', variant: 'secondary' },
  terminated: { label: 'Baixa', variant: 'destructive' },
};

// Etiquetes per als 4 codis de fàbrica — si s'afegeix un tipus nou des
// d'Admin > Taules > RRHH, es mostra el seu codi tal qual (vegeu l'ús més avall).
const contractConfig: Record<string, string> = {
  indefinite: 'Indefinit',
  temporary: 'Temporal',
  intern: 'Practiques',
  freelance: 'Autonom',
};

const RRHH_TABS = [
  { id: 'employees',   label: 'Empleats',    icon: Users,         path: '/rrhh',            color: 'blue' },
  { id: 'calendar',    label: 'Calendari',   icon: Calendar,      path: '/rrhh/calendar',    color: 'purple' },
  { id: 'absences',    label: 'Absències',   icon: Calendar,      path: '/rrhh/absences',    color: 'amber' },
  { id: 'work-logs',   label: 'Imputació',   icon: Clock,         path: '/rrhh/work-logs',   color: 'teal' },
  { id: 'trainings',   label: 'Formació',    icon: GraduationCap, path: '/rrhh/trainings',   color: 'indigo' },
  { id: 'payroll',     label: 'Nòmines',     icon: FileText,      path: '/rrhh/payroll',     color: 'green' },
  { id: 'evaluations', label: 'Avaluacions', icon: Briefcase,     path: '/rrhh/evaluations', color: 'rose' },
  { id: 'documents',   label: 'Documents',   icon: FileText,      path: '/rrhh/documents',   color: 'slate' },
];

// Mateix patró de "pestanyes com a tarjetes" que CRM.tsx/Compres — colors fixos
// literals perquè el JIT de Tailwind els trobi al codi font.
const RRHH_TAB_COLOR: Record<string, { border: string; bg: string; icon: string; iconBg: string }> = {
  blue:   { border: 'border-blue-500',   bg: 'bg-blue-50/50',   icon: 'text-blue-500',   iconBg: 'bg-blue-500/10' },
  purple: { border: 'border-purple-500', bg: 'bg-purple-50/50', icon: 'text-purple-500', iconBg: 'bg-purple-500/10' },
  amber:  { border: 'border-amber-500',  bg: 'bg-amber-50/50',  icon: 'text-amber-500',  iconBg: 'bg-amber-500/10' },
  teal:   { border: 'border-teal-500',   bg: 'bg-teal-50/50',   icon: 'text-teal-500',   iconBg: 'bg-teal-500/10' },
  indigo: { border: 'border-indigo-500', bg: 'bg-indigo-50/50', icon: 'text-indigo-500', iconBg: 'bg-indigo-500/10' },
  green:  { border: 'border-green-500',  bg: 'bg-green-50/50',  icon: 'text-green-500',  iconBg: 'bg-green-500/10' },
  rose:   { border: 'border-rose-500',   bg: 'bg-rose-50/50',   icon: 'text-rose-500',   iconBg: 'bg-rose-500/10' },
  slate:  { border: 'border-slate-500',  bg: 'bg-slate-50/50',  icon: 'text-slate-500',  iconBg: 'bg-slate-500/10' },
};

export default function RRHH() {
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtres
  const [search, setSearch] = useSessionState('rrhh:search', '');
  const [departmentFilter, setDepartmentFilter] = useSessionState('rrhh:departmentFilter', 'all');
  const [statusFilter, setStatusFilter] = useSessionState('rrhh:statusFilter', 'all');

  // Ordenacio - per defecte Codi (employeeNumber) ascendent
  const [sortBy, setSortBy] = useSessionState('rrhh:sortBy', 'employeeNumber');
  const [sortOrder, setSortOrder] = useSessionState<'ASC' | 'DESC'>('rrhh:sortOrder', 'ASC');
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  // Paginacio
  const [page, setPage] = useState(1);
  const limit = 15;

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ sortBy, sortOrder });
      if (search) params.append('search', search);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const employeesRes = await api.get<{ success: boolean; data: Employee[]; total: number }>(`/employees?${params}`);
      setEmployees(employeesRes.data);
    } catch (err) {
      console.error('[v0] Error fetching employees:', err);
      setError('Error en carregar els empleats');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [search, departmentFilter, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    setPage(1);
  }, [search, departmentFilter, statusFilter, sortBy, sortOrder]);

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortOrder === 'ASC' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };
  const SortHead = ({ field, className, align = 'left', children }: { field: string; className?: string; align?: 'left' | 'right'; children: ReactNode }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={`flex items-center gap-1 hover:text-foreground w-full ${align === 'right' ? 'justify-end' : ''}`}
      >
        {children} <SortIcon field={field} />
      </button>
    </TableHead>
  );

  const handleViewEmployee = (id: string) => {
    navigate(`/rrhh/employees/${id}`);
  };

  const handleEditEmployee = (id: string) => {
    navigate(`/rrhh/employees/${id}/edit`);
  };

  const handleNewEmployee = () => {
    navigate('/rrhh/employees/new');
  };

  const handleDeleteEmployee = async (id: string, name: string) => {
    if (!window.confirm(`Estàs segur que vols eliminar l'empleat "${name}"? Aquesta acció no es pot desfer.`)) {
      return;
    }
    
    try {
      await api.delete(`/employees/${id}`);
      // Recarregar la llista
      setEmployees(employees.filter(e => e.id !== id));
    } catch (err) {
      console.error('[v0] Error deleting employee:', err);
      alert('Error en eliminar l\'empleat');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Paginated employees
  const startIndex = (page - 1) * limit;
  const paginatedEmployees = employees.slice(startIndex, startIndex + limit);
  const totalPages = Math.ceil(employees.length / limit);

  return (
    <div className="space-y-6">
      {/* Capcalera */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Recursos Humans</h1>
          <p className="text-muted-foreground">
            Gestió d'empleats, absències i formació
          </p>
        </div>
        <Button size="sm" onClick={handleNewEmployee}>
          <Plus className="h-4 w-4 mr-2" />
          Nou Empleat
        </Button>
      </div>

      {/* Pestanyes com a tarjetes — mateix patró que CRM/Compres */}
      <div className="grid grid-cols-8 gap-3">
        {RRHH_TABS.map((tab) => {
          const Icon = tab.icon;
          const c = RRHH_TAB_COLOR[tab.color];
          const isActive = tab.path === '/rrhh'
            ? location.pathname === '/rrhh'
            : location.pathname.startsWith(tab.path);
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${
                isActive ? `${c.border} ${c.bg}` : 'border-border bg-white hover:shadow-sm'
              }`}
            >
              <div className={`p-1.5 rounded-lg shrink-0 ${c.iconBg}`}>
                <Icon className={`h-4 w-4 ${c.icon}`} />
              </div>
              <span className="text-xs font-semibold truncate">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Filtres i Llista */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Empleats
            </CardTitle>
            <span className="text-sm text-muted-foreground">{employees.length} empleats</span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtres */}
          <div className="flex flex-col md:flex-row gap-3 mt-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Cercar per nom, DNI o numero..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <ExcelFilterDropdown
                label="Departament"
                options={[
                  { value: 'Taller', label: 'Taller' },
                  { value: 'Oficina Tecnica', label: 'Oficina Tecnica' },
                  { value: 'Administracio', label: 'Administració' },
                  { value: 'Comercial', label: 'Comercial' },
                  { value: 'Direccio', label: 'Direccio' },
                ]}
                selected={departmentFilter === 'all' ? [] : departmentFilter.split(',')}
                onApply={(values) => setDepartmentFilter(values.length === 0 ? 'all' : values.join(','))}
              />
            </div>

            <ExcelFilterDropdown
              label="Estat"
              options={[
                { value: 'active', label: 'Actius' },
                { value: 'on_leave', label: 'De baixa' },
                { value: 'terminated', label: 'Baixa definitiva' },
              ]}
              selected={statusFilter === 'all' ? [] : statusFilter.split(',')}
              onApply={(values) => setStatusFilter(values.length === 0 ? 'all' : values.join(','))}
            />
          </div>

          {/* Taula */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No s&apos;han trobat empleats</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead field="employeeNumber">Codi</SortHead>
                    <SortHead field="lastName">Empleat</SortHead>
                    <SortHead field="department">Departament</SortHead>
                    <SortHead field="position">Carrec</SortHead>
                    <TableHead>Contracte</TableHead>
                    <SortHead field="hireDate">Data Alta</SortHead>
                    <SortHead field="hourlyCost" align="right">Cost/h</SortHead>
                    <SortHead field="status">Estat</SortHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEmployees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      onClick={() => handleViewEmployee(employee.id)}
                      className="cursor-pointer hover:bg-accent/50"
                    >
                      <TableCell className="font-mono text-sm">
                        {employee.employeeNumber}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {employee.firstName} {employee.lastName}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {employee.dni}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {employee.department || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground" />
                          {employee.position || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {contractConfig[employee.contractType] || employee.contractType}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(employee.hireDate)}</TableCell>
                      <TableCell className="font-mono">
                        {formatDecimal(Number(employee.hourlyCost || 0))} EUR
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusConfig[employee.status].variant}>
                          {statusConfig[employee.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditEmployee(employee.id)}
                            className="p-2 hover:bg-accent rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(employee.id, `${employee.firstName} ${employee.lastName}`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Paginació */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Mostrant {startIndex + 1}-{Math.min(startIndex + limit, employees.length)} de {employees.length} empleats
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 hover:bg-accent rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm">
                      Pagina {page} de {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 hover:bg-accent rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
